import { Auditoria } from "./Auditoria"
import { CanaisDeAviso } from "./CanaisDeAviso"
import { useEffect, useMemo, useState, type ReactNode } from "react"
import { type AlcanceDeConfiguracoes } from "../api"
import { Card, CardContent } from "../componentes/Card"
import { Skeleton } from "../componentes/Skeleton"
import { Abas, type DescricaoDeAba } from "../componentes/Abas"
import { ABAS_NO_CARTAO, CARTAO_DAS_ABAS, MAIUSCULA, RECUSA, VAZIO } from "../lib/classes"
import { cn } from "../lib/cn"
import {
  DADOS_DO_ITEM,
  ITENS_DE_CONFIGURACAO,
  grupoDoItem,
  gruposDeConfiguracao,
  itemVisivel,
  type ItemDeConfiguracao,
} from "../itens-de-configuracao"
import { Athus } from "./Athus"
import { Contas } from "./Contas"
import { PerfisDeAcesso } from "./PerfisDeAcesso"
import { CatalogoDeSistemas } from "./CatalogoDeSistemas"
import { Entidades } from "./Entidades"
import { LeituraDosContratos } from "./LeituraDosContratos"
import { Setores } from "./Setores"
import { TiposDeSetor } from "./TiposDeSetor"
import { Cargos } from "./Cargos"
import { TiposDeEntidade } from "./TiposDeEntidade"
import { FaixasDeProvidencia, LimitesEPrazos, TiposDePeca } from "./RegrasDoSistema"

type PropsDaTela = {
  alcance: AlcanceDeConfiguracoes | null
  recusa: string | null
  item: ItemDeConfiguracao | undefined
  aoTrocarItem: (item: ItemDeConfiguracao) => void
  entidade?: number
  aoAbrirEntidade?: (id: number | null, substituir?: boolean) => void
  aoSaberDaEntidade?: (sigla: string | null) => void
  perfil?: string
  aoAbrirContasDoPerfil?: (perfil: string | null, substituir?: boolean) => void
  tipo?: string
  cargo?: string
  sistema?: string
  aoAbrirFiltrada?: (
    item: ItemDeConfiguracao,
    filtros: { perfil?: string | null; cargo?: string | null; tipo?: string | null; sistema?: string | null },
    substituir?: boolean,
  ) => void
  aoAbrirContrato?: (id: number) => void
}

type NaTela = {
  alcance: AlcanceDeConfiguracoes
  atual: ItemDeConfiguracao
  aoTrocarItem: (item: ItemDeConfiguracao) => void
  entidade?: number
  aoAbrirEntidade?: (id: number | null, substituir?: boolean) => void
  aoSaberDaEntidade?: (sigla: string | null) => void
  perfil?: string
  aoAbrirContasDoPerfil?: (perfil: string | null, substituir?: boolean) => void
  tipo?: string
  cargo?: string
  sistema?: string
  aoAbrirFiltrada?: (
    item: ItemDeConfiguracao,
    filtros: { perfil?: string | null; cargo?: string | null; tipo?: string | null; sistema?: string | null },
    substituir?: boolean,
  ) => void
  aoAbrirContrato?: (id: number) => void
}

const ABAS_ANINHADAS_SEM_MOLDURA =
  "[&_[data-slot=cartao-das-abas]]:rounded-none [&_[data-slot=cartao-das-abas]]:border-x-0 [&_[data-slot=cartao-das-abas]]:border-b-0 [&_[data-slot=cartao-das-abas]]:shadow-none"


function desenhoDosItens({
  alcance,
  atual,
  aoTrocarItem,
  entidade,
  aoAbrirEntidade = () => undefined,
  aoSaberDaEntidade,
  perfil,
  aoAbrirContasDoPerfil = () => undefined,
  tipo,
  cargo,
  sistema,
  aoAbrirFiltrada = () => undefined,
  aoAbrirContrato,
}: NaTela): Record<ItemDeConfiguracao, () => ReactNode> {
  return {
    entidades: () => (
      <Entidades
        alcance={alcance}
        ativo={atual === "entidades"}
        tipo={atual === "entidades" ? tipo : undefined}
        aoFiltrarTipo={(novo) => aoAbrirFiltrada("entidades", { tipo: novo }, true)}
        sistema={atual === "entidades" ? sistema : undefined}
        aoFiltrarSistema={(novo) => aoAbrirFiltrada("entidades", { sistema: novo }, true)}
        entidade={entidade}
        aoAbrirEntidade={aoAbrirEntidade}
        aoSaberDaEntidade={aoSaberDaEntidade}
        aoAbrirConfiguracao={aoTrocarItem}
      />
    ),
    setores: () => (
      <Setores
        alcance={alcance}
        tipo={atual === "setores" ? tipo : undefined}
        aoFiltrarTipo={(novo) => aoAbrirFiltrada("setores", { tipo: novo }, true)}
      />
    ),
    contas: () => (
      <Contas
        perfil={perfil}
        aoFiltrarPerfil={(novo) => aoAbrirContasDoPerfil(novo, true)}
        cargo={atual === "contas" ? cargo : undefined}
        aoFiltrarCargo={(novo) => aoAbrirFiltrada("contas", { cargo: novo }, true)}
      />
    ),
    papeis: () => <PerfisDeAcesso aoAbrirContasDoPerfil={(papel) => aoAbrirContasDoPerfil(papel)} />,
    "tipos-de-entidade": () => (
      <TiposDeEntidade alcance={alcance} aoVerOndeEUsado={(nome) => aoAbrirFiltrada("entidades", { tipo: nome })} />
    ),
    "tipos-de-setor": () => <TiposDeSetor alcance={alcance} aoVerOndeEUsado={(nome) => aoAbrirFiltrada("setores", { tipo: nome })} />,
    cargos: () => <Cargos alcance={alcance} aoVerOndeEUsado={(nome) => aoAbrirFiltrada("contas", { cargo: nome })} />,
    sistemas: () => (
      <CatalogoDeSistemas alcance={alcance} aoVerOndeEUsado={(sigla) => aoAbrirFiltrada("entidades", { sistema: sigla })} />
    ),
    parametros: () => <LimitesEPrazos alcance={alcance} />,
    faixas: () => <FaixasDeProvidencia alcance={alcance} />,
    tipos: () => <TiposDePeca />,
    athus: () => <Athus />,
    leitura: () => <LeituraDosContratos aoAbrirContrato={aoAbrirContrato} />,
    canais: () => <CanaisDeAviso />,
    auditoria: () => <Auditoria />,
    senha: () => <LimitesEPrazos alcance={alcance} assunto="Senha" />,
  }
}

export function Configuracoes({
  alcance,
  recusa,
  item,
  aoTrocarItem,
  entidade,
  aoAbrirEntidade,
  aoSaberDaEntidade,
  perfil,
  aoAbrirContasDoPerfil,
  tipo,
  cargo,
  sistema,
  aoAbrirFiltrada,
  aoAbrirContrato,
}: PropsDaTela) {
  const [abertos, definirAbertos] = useState<ReadonlySet<ItemDeConfiguracao>>(new Set())
  const grupos = useMemo(() => (alcance === null ? [] : gruposDeConfiguracao(alcance)), [alcance])
  const atual = alcance === null ? undefined : itemVisivel(grupos, item)

  useEffect(() => {
    if (atual === undefined || abertos.has(atual)) return
    definirAbertos(new Set([...abertos, atual]))
  }, [atual, abertos])

  if (recusa !== null) {
    return (
      <Card>
        <CardContent>
          <p className={cn(RECUSA, MAIUSCULA)}>{recusa}</p>
        </CardContent>
      </Card>
    )
  }
  if (alcance === null) {
    return (
      <Card aria-busy="true">
        <CardContent className="flex flex-col gap-3">
          <Skeleton className="h-8 w-1/2" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
        </CardContent>
      </Card>
    )
  }
  if (atual === undefined) {
    return (
      <Card>
        <CardContent>
          <p className={VAZIO}>Nenhum item de Configurações está liberado para a sua conta.</p>
        </CardContent>
      </Card>
    )
  }

  const grupo = grupoDoItem(grupos, atual)
  const desenhos = desenhoDosItens({
    alcance,
    atual,
    aoTrocarItem,
    entidade,
    aoAbrirEntidade,
    aoSaberDaEntidade,
    perfil,
    aoAbrirContasDoPerfil,
    tipo,
    cargo,
    sistema,
    aoAbrirFiltrada,
    aoAbrirContrato,
  })
  const visiveis = abertos.has(atual) ? abertos : new Set([...abertos, atual])
  const abas: DescricaoDeAba<ItemDeConfiguracao>[] = (grupo?.itens ?? []).map((chave) => ({
    chave,
    rotulo: DADOS_DO_ITEM[chave].rotulo,
    dica: DADOS_DO_ITEM[chave].dica,
    icone: DADOS_DO_ITEM[chave].icone,
  }))
  const comAbas = grupo !== undefined && abas.length > 1

  const paineis = ITENS_DE_CONFIGURACAO.filter((chave) => visiveis.has(chave)).map((chave) => (
    <div
      key={chave}
      className="flex flex-col gap-2 [&[hidden]]:hidden"
      role={comAbas ? "tabpanel" : undefined}
      id={`bloco-configuracoes-${chave}`}
      aria-labelledby={comAbas ? `aba-configuracoes-${chave}` : undefined}
      hidden={chave !== atual}
    >
      {desenhos[chave]()}
    </div>
  ))

  if (!comAbas) {
    return <div className="flex min-w-0 flex-col gap-2">{paineis}</div>
  }

  return (
    <div data-slot="cartao-das-abas" className={cn(CARTAO_DAS_ABAS, ABAS_ANINHADAS_SEM_MOLDURA, "min-w-0")}>
      <Abas
        nome="configuracoes"
        rotulo={grupo.rotulo}
        abas={abas}
        atual={atual}
        aoTrocar={aoTrocarItem}
        classe={ABAS_NO_CARTAO}
        classeDaLista="max-[767px]:flex-nowrap max-[767px]:overflow-x-auto max-[767px]:[&>*]:flex-none"
      />
      {paineis}
    </div>
  )
}
